import { ITableQueryData } from "types/frontend_models/table/ITableQueryData";
import { ITableFilter, FilterOperators } from "types/frontend_models/filtering/filters";
import { isNullOrEmpty } from "functions/extensions/StringExtensions";
import { defaultDataTablePageSize, defaultDataTablePagesizeOptions } from "functions/tables/TableFunctions";
import { IKeyValuePair } from "types/general/IKeyValuePair";
import { convertStringToFilterOperator } from "functions/converters/FilterOperatorConverters";

/* DEFAULT URL FUNCTIONS */
/** Decodes default url search params like ?id=2500&ownerId=cst */
export function searchToParams(search: string): IKeyValuePair[]
{
	let correctedSearch = search;
	if (correctedSearch.startsWith('?')) correctedSearch = correctedSearch.substring(1);


	let searchParams: IKeyValuePair[] = [];
	const splitted = correctedSearch.split('&');
	splitted.forEach(s =>
	{
		let paramSplit = s.split('=');
		if (paramSplit.length === 2) {
			searchParams.push({ key: paramSplit[0], value: paramSplit[1] });
		}
	});

	return searchParams;
}



/* TABLE RELATED URL FUNCTIONS */

/** Converts table query to url search params */
export function tableQueryToSearch(queryData: ITableQueryData): string | null
{
	let search = '';

	if (queryData.pageSize) {
		search += 'pagesize=' + queryData.pageSize.toString();
	}
	if (queryData.currentPage) {
		search = addAndSignIfNotEmpty(search);
		search += 'currentpage=' + queryData.currentPage.toString();
	}
	if (queryData.filters && queryData.filters.length !== 0) {
		search = addAndSignIfNotEmpty(search);
		search += 'filters=';
		const lastIndex = queryData.filters.length - 1;
		queryData.filters.forEach((filter, index) =>
		{
			if (!isNullOrEmpty(filter.value)) {
				search += filter.key + ' ';
				search += filter.operator.toString() + ' ';
				search += filter.value;
				if (index !== lastIndex) search += ','; // the arguments separator	
			}
		});
	}
	if (queryData.sorted && queryData.sorted !== '') {
		search = addAndSignIfNotEmpty(search);
		search += 'sorts=' + queryData.sorted;
	}

	return search !== '' ? search : null;
}

/** Adds & before the next parameter if there are any preceeding params */
function addAndSignIfNotEmpty(value: string): string
{
	if (value !== '') return value + '&';
	return value;
}

/**
 * @param search The url search. May include the '?' character.
 * @param previousQueryData Previous table query data.
 * @param ignorePagesizeOptions Ignore the default available page options, which makes high or low values possible.
 */
export function searchToTableQuery(
	search: string,
	previousQueryData?: ITableQueryData,
	ignorePageSizeOptions = false
): ITableQueryData
{


	let queryData: ITableQueryData;
	if (previousQueryData) {
		queryData = previousQueryData;
	} else {
		queryData = {
			filters: [],
			sorted: '',
			pageSize: 10,
			currentPage: 1
		}
	}

	if (search.startsWith('?')) {
		search = search.substr(1);
	}

	const params = searchToParams(search);
	params.forEach(param =>
	{
		if (param.key && param.value) {
			switch (param.key) {
				case 'pagesize': {
					if (ignorePageSizeOptions || defaultDataTablePagesizeOptions.indexOf(param.value) !== -1) {
						queryData.pageSize = Number(param.value);
					} else {
						queryData.pageSize = defaultDataTablePageSize;
					}
					break;
				}
				case 'currentpage': {
					queryData.currentPage = Number(param.value);
					break;
				}
				case 'filters': {
					queryData.filters = parseFilters(param.value);
					break;
				}
				case 'sorts': {
					queryData.sorted = param.value;
					break;
				}
			}
		}
	});

	return queryData;
}

function parseFilters(value: string): ITableFilter[]
{
	// eg filters=id eq 2500,param2 eq 1
	const splitted = value.split(',');
	const filters: ITableFilter[] = [];

	splitted.forEach(s =>
	{
		// E.g. id eq 2500 -> [id, eq, 2500]
		const params = s.split(' ');
		if (params.length === 3) {
			let filter: ITableFilter = {
				key: '',
				operator: FilterOperators.undefined,
				value: ''
			}
			params.forEach((param, index) =>
			{
				switch (index) {
					case 0: {
						filter.key = param;
						break;
					}
					case 1: {
						filter.operator = convertStringToFilterOperator(param);
						break;
					}
					case 2: {
						filter.value = param;
					}
				}
			});

			filters.push(filter);
		} else {
			console.warn('Filter not parsed, wrong format, it should have 3 params. Filter value:', s);
		}
	});

	return filters;
}

/** Prefixes correct root path to the image path based on the env variable */
export function fixPublicPath(path: string): string
{
	let rootPath = process.env.REACT_APP_ROOT_PATH;
	// If the root path variable is not set to anyhting, the passed path argument is correct
	if (isNullOrEmpty(rootPath) || rootPath === '/') return path;

	if (!rootPath.startsWith('/')) rootPath = '/' + rootPath;
	if (!path.startsWith('/')) path = '/' + path;

	return rootPath + path;
}